/**
 * @file SectionHeaderFields.tsx
 * @description Шапка раздела над колонками (phase19.16 Р2; uiux-design-r2 §4
 * «Шапка раздела»): надпись, заголовок, уровень (H1 — с предупреждением о
 * втором главном заголовке на странице), значок, линия до ссылки справа,
 * «Все новинки ›» (текст и куда ведёт), выравнивание.
 *
 * Приёмка Р2а §7.17 п. 3 (uiux-fonts-usage §4): внизу — подсказка о шрифтах
 * шапки (`SECTION_HEADER_FONT_PARTS`): строки надписи и ссылки — только
 * когда они есть.
 *
 * SB120 этап 1 (uiux-sb120-stage1 §2.7): вместо галочки линии — подгруппа
 * «Вид шапки» (`HeaderLookFields`: цвет шапки, толщина и цвет линии).
 *
 * SB120 этап 3а (uiux-sb120-stage3 §2.2): подгруппа «Шрифт шапки» — цвет,
 * шрифт, размер по частям «Надпись | Заголовок | Ссылка» (`header.typo`,
 * `ElementTypeFields`); цвет части поверх общего цвета шапки.
 */

import { DEFAULT_SITE_THEME, SECTION_HEADER_FONT_PARTS, SECTION_TEXT_LIMITS, type FontPart, type SectionBg, type SectionHeader } from "@chaiberi/shared";

import type { OtherH1 } from "../../../../lib/site-builder/page-h1";

import { Label } from "../../../ui/label";
import { SettingsSubgroup } from "../../../ui/settings-groups";
import { CheckRow } from "../../../site-view/CheckRow";
import { Segmented } from "../../../site-view/Segmented";
import { IconPicker } from "../fields/IconPicker";
import { SectionTargetField } from "../fields/SectionTargetField";
import { SectionTextField } from "../fields/SectionTextField";
import { ElementFontNote } from "../elements/ElementFontNote";
import { HeadingLevelField } from "../elements/HeadingLevelField";
import { ElementTypeFields } from "../elements/ElementTypeFields";
import { useSectionForm } from "../section-form-context";
import { HeaderLookFields } from "./HeaderLookFields";
import { typoStatus } from "../../../../lib/site-builder/element-groups";
import { withTypo } from "../../../../lib/site-builder/element-typo";
import { sectionBgSample } from "../../../../lib/site-builder/text-style";
import { headerTypoBase, headingSizePresets } from "../../../../lib/site-builder/typo-base";

const ALIGN = [
  { value: "left" as const, label: "Слева" },
  { value: "center" as const, label: "По центру" },
  { value: "right" as const, label: "Справа" },
];

/** Короткие подписи частей для переключателя (на 360 три кнопки в ряд 48). */
const SHORT_PART: Readonly<Record<string, string>> = { eyebrow: "Надпись", title: "Заголовок", link: "Ссылка" };

/** Части шапки с текстом: без надписи и без ссылки их строк нет. */
function headerFontParts(header: SectionHeader): FontPart[] {
  return SECTION_HEADER_FONT_PARTS.filter((p) => (p.role === "eyebrow" ? !!header.eyebrow : p.role === "body" ? !!header.link : true));
}

interface Props {
  header: SectionHeader | undefined;
  onChange: (header: SectionHeader | undefined) => void;
  /** Блок с другим главным заголовком на странице. */
  otherH1: OtherH1 | null;
  /** Фон секции — для контраста цвета шапки. */
  bg: SectionBg;
}

export function SectionHeaderFields({ header, onChange, otherH1, bg }: Props) {
  const { theme, elementWarnings } = useSectionForm();
  if (!header) {
    return (
      <CheckRow checked={false} onChange={() => onChange({ title: "Заголовок раздела", level: 2, line: { width: 0 } })}>
        Шапка раздела над колонками — заголовок и ссылка «Смотреть все»
      </CheckRow>
    );
  }
  const set = (patch: Partial<SectionHeader>) => onChange({ ...header, ...patch });
  const T = SECTION_TEXT_LIMITS;
  return (
    <div className="flex flex-col gap-3">
      <CheckRow checked onChange={() => onChange(undefined)}>
        Шапка раздела над колонками
      </CheckRow>
      <SectionTextField label="Надпись над заголовком" path="header.eyebrow" value={header.eyebrow} max={T.eyebrow} hint="Пусто — без надписи." onChange={(eyebrow) => set({ eyebrow: eyebrow || undefined })} />
      <SectionTextField label="Заголовок раздела" path="header.title" value={header.title} max={T.heading} onChange={(title) => set({ title })} />
      <HeadingLevelField level={header.level} otherH1={otherH1} onChange={(level) => set({ level })} />
      <IconPicker value={header.icon} onChange={(icon) => set({ icon: icon ?? undefined })} />
      <CheckRow checked={!!header.link} onChange={(on) => set({ link: on ? { label: "Смотреть все", target: { type: "system", route: "CATALOG" } } : undefined })}>
        Ссылка справа
      </CheckRow>
      {header.link ? (
        <>
          <SectionTextField label="Текст ссылки" path="header.link.label" value={header.link.label} max={T.label} onChange={(label) => header.link && set({ link: { ...header.link, label } })} />
          <SectionTargetField value={header.link.target} onChange={(target) => header.link && target && set({ link: { ...header.link, target } })} />
        </>
      ) : null}
      <Label>Выравнивание заголовка</Label>
      <Segmented aria-label="Выравнивание заголовка раздела" options={ALIGN} value={header.align ?? "left"} onChange={(align) => set({ align })} />
      <HeaderLookFields header={header} bg={bg} onChange={onChange} />
      <SettingsSubgroup title="Шрифт шапки" status={typoStatus(elementWarnings, ["header.typo"])}>
        <ElementTypeFields
          parts={headerFontParts(header).map((p) => ({ ...p, part: (p.key && SHORT_PART[p.key]) || p.part }))}
          typo={header.typo}
          onChange={(typo) => onChange(withTypo(header, typo))}
          path="header"
          baseOf={(key) => headerTypoBase(header, key, theme)}
          presetsOf={(key) => (key === "title" ? headingSizePresets(theme) : undefined)}
          elementBg={sectionBgSample(bg, (theme ?? DEFAULT_SITE_THEME).colors)}
          colorAutoLabel="Как у шапки"
        />
      </SettingsSubgroup>
      <ElementFontNote parts={headerFontParts(header)} backLabel="Шапка раздела" />
    </div>
  );
}
